// Контроль агента (requirements Кн1–Кн8, model quint/specs/agent_control.qnt): поля заметки, шлюзы переходов по этапам задач,
// рекомендации «что делать сейчас» и «что нужно дальше». Проверка формальная — есть ли поле и похоже ли значение; текст не «понимается».
// One file for the service and the widget: the card shows the same checklist the gate enforces.

import { STAGE_TITLES, STAGES, canonicalStatus, stageOf, EPIC_STATUS_TITLES, type EpicStatus, type Stage } from './board';

/** Кн2: the fields of a note — lines «Ключ: значение». */
export const FIELD_KEYS = ['requirement', 'model', 'context', 'critical', 'contracts', 'run', 'infra', 'ux', 'commit', 'branch', 'version'] as const;
export type FieldKey = (typeof FIELD_KEYS)[number];
/** What a gate may ask for: a field, the green model (state of the «Модель» field) or the note of the transition itself. */
export type CheckKey = FieldKey | 'green' | 'note';

export const FIELD_LABELS: Record<FieldKey, string> = {
  requirement: 'Требование',
  model: 'Модель',
  context: 'Контекст',
  critical: 'Критический путь',
  contracts: 'Стыки',
  run: 'Прогон',
  infra: 'Инфра',
  ux: 'UX',
  commit: 'Коммит',
  branch: 'Ветка',
  version: 'Версия',
};

export const CHECK_LABELS: Record<CheckKey, string> = {
  ...FIELD_LABELS,
  green: 'Модель green',
  note: 'Заметка к переходу',
};

/** A line to paste into the note: the field and a value of the right shape. */
export const CHECK_EXAMPLES: Record<CheckKey, string> = {
  requirement: 'Требование: Хх1',
  model: 'Модель: имя_модели red',
  green: 'Модель: имя_модели green',
  context: 'Контекст: имя ограниченного контекста (карта — quint/CONTEXTS.md)',
  critical: 'Критический путь: модель.свойство (нет такого: «Критический путь: нет — причина»)',
  contracts: 'Стыки: имя контракта (нет стыков: «Стыки: нет — причина»)',
  run: 'Прогон: bash quint/run.sh имя_модели — ок',
  infra: 'Инфра: ок — отчёт <номер> (готовую строку даёт ready.mjs line infra; иначе «Инфра: не проверено — причина» или «Инфра: не требуется — причина»)',
  ux: 'UX: ок — отчёт <номер> (готовую строку даёт ready.mjs line ux; иначе «UX: не проверено — причина» или «UX: не требуется — причина»)',
  commit: 'Коммит: abc1234',
  branch: 'Ветка: fix/имя',
  version: 'Версия: abc1234',
  note: 'заметка: что сделано и где (при «не будет сделано», «дубль», «закрыто» — причина)',
};

const CODE = '[A-Za-zА-Яа-яЁё]{1,4}\\d{1,5}';
/** Гт1: «Инфра» / «UX» — «ок — отчёт <номер>» набора или честное «не проверено» / «не требуется» с причиной; «что проверено — ок» не проверка. */
const CHECKED = /^(?:ок\s*(?:—|–|-)\s*отчёт\s+[0-9a-f]{6,16}(?:\s.*)?|(?:не проверено|не требуется)\s*(?:—|–|-|:)\s*\S.{2,})$/i;
const NO_REASON = '(?:нет\\s*(?:—|–|-)\\s*\\S.{2,})';
const PATH = '[a-z][a-z0-9_]{1,63}\\.[A-Za-z][A-Za-z0-9_]{1,63}';
const FORMAT: Record<FieldKey, RegExp> = {
  requirement: new RegExp(`^${CODE}(\\s*,\\s*${CODE})*$`),
  model: /^[a-z][a-z0-9_]{1,63}\s+(red|green)$/i,
  // Гт2: the name of a bounded context of quint/CONTEXTS.md
  context: /^[A-Za-zА-Яа-яЁё][\wА-Яа-яЁё.-]{1,63}$/,
  // Гт3: model.property (several with commas) or «нет — причина»
  critical: new RegExp(`^(?:${PATH}(?:\\s*,\\s*${PATH})*|${NO_REASON})$`),
  // Гт4: contract names (several with commas) or «нет — причина»
  contracts: new RegExp(`^(?:[\\w./-]{2,100}(?:\\s*,\\s*[\\w./-]{2,100})*|${NO_REASON})$`),
  run: /^\S.{2,}?\s*(?:—|–|-|→|->|=|:)\s*(?:ок|ok|green|passed|exit 0)$/i,
  infra: CHECKED,
  ux: CHECKED,
  commit: /^[0-9a-f]{7,40}$/i,
  branch: /^[A-Za-z0-9._/-]{1,100}$/,
  version: /^[0-9a-f]{7,40}$/i,
};

const LINE = /^[ \t>*-]*(Требование|Модель|Контекст|Критический путь|Стыки|Прогон|Инфра|UX|Коммит|Ветка|Версия)[ \t]*:[ \t]*(.+?)[ \t]*$/gim;
const KEY_OF_WORD: Record<string, FieldKey> = {
  требование: 'requirement',
  модель: 'model',
  контекст: 'context',
  'критический путь': 'critical',
  стыки: 'contracts',
  прогон: 'run',
  инфра: 'infra',
  ux: 'ux',
  коммит: 'commit',
  ветка: 'branch',
  версия: 'version',
};

/** What a report has: the value of each field, or null when it was never given or the last value is of the wrong shape. */
export type ControlFields = Record<FieldKey, string | null>;

/**
 * Кн2: the texts are the notes of the report in order, the transition's own note last. The last value of a field wins; a value of the
 * wrong shape counts as «not attached» (and replaces an earlier right one: the person wrote it later).
 */
export function parseControlFields(texts: readonly (string | null | undefined)[]): ControlFields {
  const last: Partial<Record<FieldKey, string>> = {};
  for (const text of texts) {
    if (!text) continue;
    for (const match of text.matchAll(LINE)) last[KEY_OF_WORD[match[1].toLowerCase()]] = match[2];
  }
  const out = Object.fromEntries(FIELD_KEYS.map((key) => [key, null])) as ControlFields;
  for (const key of FIELD_KEYS) {
    const value = last[key];
    if (value !== undefined && FORMAT[key].test(value)) out[key] = value;
  }
  return out;
}

/** The model field is green. */
export const modelIsGreen = (fields: ControlFields): boolean => /\sgreen$/i.test(fields.model ?? '');

/** Кн3: the fields each stage adds to the ones before it. */
export const STAGE_OWN: Record<Stage, CheckKey[]> = {
  backlog: [],
  requirements: [],
  validation: ['requirement', 'model', 'context', 'critical'],
  implementation: ['green', 'run', 'infra', 'ux', 'contracts'],
  deploy: ['commit', 'branch'],
  done: ['version', 'note'],
};

const FINAL_OTHER = ['wont_fix', 'duplicate', 'closed'];

/** Кн3, Кн6: what entering `to` needs — the fields of every stage up to it; «not done» resolutions — only a note. */
export function gateNeeds(to: string): CheckKey[] {
  const status = canonicalStatus(to);
  if (FINAL_OTHER.includes(status)) return ['note'];
  const target = STAGES.indexOf(stageOf(status));
  const out: CheckKey[] = [];
  for (const stage of STAGES.slice(0, target + 1)) out.push(...STAGE_OWN[stage]);
  return out;
}

/** Кн3: a gate stands on a move forward and on «not done» → «done»; back and within a stage there is none. */
export function gated(from: string, to: string): boolean {
  const a = canonicalStatus(from);
  const b = canonicalStatus(to);
  if (a === b) return false;
  if (b === 'fixed' && FINAL_OTHER.includes(a)) return true;
  return STAGES.indexOf(stageOf(b)) > STAGES.indexOf(stageOf(a));
}

export const hasCheck = (key: CheckKey, fields: ControlFields, hasNote: boolean): boolean =>
  key === 'note' ? hasNote : key === 'green' ? modelIsGreen(fields) && fields.model !== null : fields[key] !== null;

/** Кн3: what the move lacks; empty when it passes or has no gate. */
export function missingFor(from: string, to: string, fields: ControlFields, hasNote: boolean): CheckKey[] {
  if (!gated(from, to)) return [];
  return gateNeeds(to).filter((key) => !hasCheck(key, fields, hasNote));
}

const stageWord = (status: string): string => STAGE_TITLES[stageOf(status)];

/** Кн5: the refusal — why, and which lines to attach. */
export function gateMessage(from: string, to: string, missing: readonly CheckKey[]): string {
  const lacks = missing.map((key) => CHECK_LABELS[key]).join(', ');
  const lines = missing.map((key) => `«${CHECK_EXAMPLES[key]}»`).join('; ');
  return `Переход «${stageWord(from)}» → «${stageWord(to)}» не принят: не хватает — ${lacks}. Приложите в заметке: ${lines}. Что делать на этапе — next_steps.`;
}

/** Кн1: one item of the checklist. */
export interface CheckItem {
  key: string;
  label: string;
  done: boolean;
  example: string;
  /** Written with the move itself (the note): cannot be ticked before it. */
  withMove?: boolean;
}

export interface StepsView {
  stage: string;
  stageTitle: string;
  /** The final stage: nothing is left to do. */
  final: boolean;
  /** Кн1: what to do now — short points. */
  doNow: string[];
  /** Кн1: what moving on needs — the next stage and the checklist of its gate. */
  next: { stage: string; title: string; checks: CheckItem[] } | null;
}

const DO_NOW: Record<Stage, string[]> = {
  backlog: [
    'Репорт ещё не взят: код и статус не менять.',
    'Исследовать репорт и вернуться к человеку с отчётом (причина, план, риски).',
    'Брать в работу — после переноса в «Требования» (обычно переносит человек).',
  ],
  requirements: [
    'Найти требование в docs/requirements*.md и каталоге или записать новое (код, было/стало).',
    'Написать модель Quint, прогнать на текущем поведении (red): bash quint/run.sh <модель>.',
    'В заметке перехода: «Требование: <код>» и «Модель: <имя> red».',
  ],
  validation: [
    'Довести модель до green: свойства, мутанты, крайние случаи (VERIFY=1, MUTATE=1).',
    'Закоммитить требование и модель отдельным коммитом, без кода.',
    'Назвать ограниченный контекст (quint/CONTEXTS.md), критический путь (модель.свойство) и стыки с другими контекстами (контракты).',
    'Проверить инфраструктуру (ready.mjs infra) и экран (ready.mjs ux); готовые строки дают ready.mjs line infra и ready.mjs line ux.',
    'В заметке перехода: «Модель: <имя> green», «Прогон: <команда> — ок», «Контекст: <имя>», «Критический путь: <модель.свойство>», «Стыки: <контракт>», «Инфра: ок — отчёт <номер>» и «UX: ок — отчёт <номер>». Не удалось проверить — честно «не проверено — причина»; нечего проверять — «не требуется — причина».',
  ],
  implementation: [
    'Написать код и тесты; в сообщении коммита — «Требование: <код>».',
    'Прогнать проверки проекта (npm run check) и приёмку модели.',
    'В заметке перехода: «Коммит: <хэш>» и «Ветка: <имя>».',
  ],
  deploy: [
    'Выложить сборку; дождаться, пока сайт отдаёт новую версию.',
    'Проверить версию на сайте и сам сценарий из репорта.',
    'В заметке перехода в «Сделано»: «Версия: <хэш>» и что сделано и где.',
  ],
  done: [
    'Задача закрыта: ничего делать не нужно.',
    'Вернуть можно только в «Бэклог» или «Реализацию» — с причиной в заметке.',
  ],
};

const NEXT_STAGE: Record<Stage, Stage | null> = {
  backlog: 'requirements',
  requirements: 'validation',
  validation: 'implementation',
  implementation: 'deploy',
  deploy: 'done',
  done: null,
};

/** Кн1: the recommendation for a report in `status` with the fields it has so far. */
export function stepsFor(status: string, fields: ControlFields): StepsView {
  const stage = stageOf(status);
  const nextStage = NEXT_STAGE[stage];
  const next = nextStage
    ? {
        stage: nextStage,
        title: STAGE_TITLES[nextStage],
        checks: gateNeeds(nextStage === 'done' ? 'fixed' : nextStage).map(
          (key): CheckItem => ({
            key,
            label: CHECK_LABELS[key],
            done: key === 'note' ? false : hasCheck(key, fields, false),
            example: CHECK_EXAMPLES[key],
            ...(key === 'note' ? { withMove: true } : {}),
          }),
        ),
      }
    : null;
  return { stage, stageTitle: STAGE_TITLES[stage], final: stage === 'done', doNow: DO_NOW[stage], next };
}

/** Кн8: what an epic has: reports linked and reports still open. */
export interface EpicFacts {
  linked: number;
  open: number;
}

const EPIC_DO_NOW: Record<EpicStatus, string[]> = {
  todo: ['Эпик ещё не начат: репорты не трогать.', 'Привязать к эпику репорты, которые он решает (link_report_to_epic).'],
  discovery: ['Разобраться, что решаем: собрать репорты, причины, риски.', 'Вернуться к человеку с выводом; статус переносит человек.'],
  discovery_done: ['Решение принято: убедиться, что у эпика есть репорты задач.', 'Вести задачи по этапам (next_steps по номеру репорта).'],
  delivery: ['Довести репорты эпика до «Сделано» (next_steps по каждому).', 'Закрывать эпик с резолюцией, когда открытых репортов не осталось.'],
  delivery_done: ['Эпик закрыт: ничего делать не нужно.', 'Вернуть можно в любой этап — со снятием резолюции.'],
};

const EPIC_NEXT: Record<EpicStatus, EpicStatus | null> = {
  todo: 'discovery',
  discovery: 'discovery_done',
  discovery_done: 'delivery',
  delivery: 'delivery_done',
  delivery_done: null,
};

/** Кн1, Кн8: the recommendation for an epic. */
export function epicStepsFor(status: EpicStatus, facts: EpicFacts): StepsView {
  const nextStatus = EPIC_NEXT[status];
  const checks: CheckItem[] = [];
  if (nextStatus === 'delivery' || nextStatus === 'delivery_done') {
    checks.push({ key: 'links', label: 'Репорт в эпике', done: facts.linked > 0, example: 'link_report_to_epic: связать репорт с эпиком' });
  }
  if (nextStatus === 'delivery_done') {
    checks.push({ key: 'closed', label: 'Открытых репортов нет', done: facts.open === 0 && facts.linked > 0, example: 'довести репорты эпика до «Сделано»' });
    checks.push({ key: 'resolution', label: 'Резолюция', done: false, example: 'resolution: fixed, wont_fix или closed', withMove: true });
  }
  return {
    stage: status,
    stageTitle: EPIC_STATUS_TITLES[status],
    final: status === 'delivery_done',
    doNow: EPIC_DO_NOW[status],
    next: nextStatus ? { stage: nextStatus, title: EPIC_STATUS_TITLES[nextStatus], checks } : null,
  };
}

/** Кн8: the gate of the agent entering Delivery of an epic: at least one linked report. */
export function epicGateProblem(to: string, facts: EpicFacts): string | null {
  if (to === 'delivery' && facts.linked === 0) {
    return 'Эпик не переведён в Delivery: в нём нет ни одного репорта. Свяжите репорт с эпиком (link_report_to_epic) и повторите. Что делать на этапе — next_steps.';
  }
  return null;
}
